import { useRef, type ReactNode } from "react";
import {
  Bug,
  ArrowRight,
  FileText,
  MessageSquare,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import {
  getHostLabel,
  buildBugToErBridgeCommand,
} from "@/lib/chat/cli-guidance";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is the Bug-to-ER Bridge?                                    */
/* -------------------------------------------------------------------------- */

export function WhatIsBugBridgeStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What does the Bug-to-ER Bridge do?">
        <p className="text-sm leading-7 text-muted-foreground">
          The Bug-to-ER Bridge connects FogBugz to SpecTree. You give it a FogBugz case
          ID, and it reads the case details — title, description, priority, severity,
          status — then hands everything to the{" "}
          <strong className="text-foreground/90">Request Formulator</strong> which runs a
          guided interview to fill any gaps and submit a high-quality Epic Request.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          The result is a{" "}
          <strong className="text-foreground/90">Bug-Sourced Epic Request</strong> with the
          FogBugz case URL linked for traceability.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="How it works">
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bug className="h-4 w-4 text-rose-400" />
            <span>FogBugz case</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Zap className="h-4 w-4 text-rose-400" />
            <span>Bridge extracts</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MessageSquare className="h-4 w-4 text-blue-400" />
            <span>Formulator interviews</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4 text-emerald-400" />
            <span>Epic Request</span>
          </div>
        </div>
        <p className="mt-3 text-sm leading-7 text-muted-foreground">
          The Bridge is a thin coordinator — it never writes the Epic Request itself. The
          Request Formulator owns the interview, duplicate check, preview, submission, and
          compliance audit.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Prerequisites">
        <p className="text-sm leading-7 text-muted-foreground">
          Both MCP servers must be connected:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={Bug}
            title="FogBugz MCP"
            description="The FogBugz MCP tools must be configured in your Copilot settings so the Bridge can read case data."
            accentClass="text-rose-400"
          />
          <InfoCard
            icon={ShieldCheck}
            title="SpecTree MCP"
            description="The SpecTree MCP server must be connected so the Formulator can submit the Epic Request."
            accentClass="text-rose-400"
          />
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Run the Bug Bridge                                               */
/* -------------------------------------------------------------------------- */

export function RunBugBridgeStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Get the FogBugz case ID">
        <p className="text-sm leading-7 text-muted-foreground">
          Find the case number (ixBug) from FogBugz. {"It's"} the numeric ID you see in the
          URL or case header — for example, <strong className="text-foreground/90">Case 1234</strong>.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Invoke the agent">
        <p className="text-sm leading-7 text-muted-foreground">
          In your {getHostLabel(host)} session, call the Bug-to-ER Bridge agent with the case ID:
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText={buildBugToErBridgeCommand(1234, host)}>
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-foreground/70">{buildBugToErBridgeCommand(1234, host)}</span>
          </p>
        </CopyableTerminal>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          The Bridge fetches the case, extracts all available fields (title, repro steps,
          priority, severity, status, project), and identifies which fields are missing so
          the Formulator knows what to ask you about.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Complete the interview">
        <p className="text-sm leading-7 text-muted-foreground">
          The Request Formulator takes over and runs a compressed interview — only asking
          about fields that were missing from the FogBugz case. Common questions include:
        </p>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-rose-400" />
            What is the proposed solution? (FogBugz cases describe the problem, not the fix)
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-rose-400" />
            How many users are affected?
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-rose-400" />
            What are the success metrics for the fix?
          </p>
        </div>
        <p className="mt-3 text-sm leading-7 text-muted-foreground">
          After submission, the Epic Request includes the originating FogBugz case URL for
          full traceability. You can then route it to{" "}
          <strong className="text-foreground/90">{host === "copilot" ? "@planner" : "@agent-spectree:planner"}</strong> to generate an execution
          plan.
        </p>
      </NumberedInstructionLast>
    </div>
  );
}
